import {
  ElementRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTour } from '@reactour/tour';
import { dayjs } from 'utils/providers/Timezone';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { Filter, Searchbox } from 'ui/components/form';
import { cn, sanitizeParams } from 'utils/helpers';
import { useGetAccountUsers } from 'modules/account/contact/hooks';
import { useQueryParams } from 'hooks';
import FILTER_TYPES from 'ui/components/form/FilterForm/filterTypes';
import { Dropdown, PageHeader, Tabs } from 'ui/components/nav';
import { useGetTasks } from 'modules/account/sales/hooks';
import c, { DATE_OPTIONS, SIZE } from 'utils/constants';
import useModal from 'ui/partials/Modal/useModal';
import { debounce, isEmpty, isString, size } from 'lodash';
import { Lead, Task } from 'utils/types';
import { useCan as useCanRolePermissions } from 'utils/providers/RolePermission';
import { useProvider } from 'utils/providers/Provider';
import { Button } from 'ui/components/common';
import { TOUR_SELECTOR } from 'ui/partials/Tour';

import _c, { DEMO_ACCOUNT_USER, DEMO_CONTACT } from '../../utils/constants';
import { CreateTaskModal, TaskCard, TaskTile } from '../../components';

const { ACTIONS, PERMISSION_KEYS } = c;

const FILTER_OPTIONS = {
  issuedAt: FILTER_TYPES.date,
  leadId: FILTER_TYPES.accountUsers,
  dueAt: FILTER_TYPES.date,
  status: FILTER_TYPES.taskStatus,
  type: FILTER_TYPES.taskType,
};

const Tasks = () => {
  const canAdd = useCanRolePermissions(ACTIONS.CREATE, PERMISSION_KEYS.TASKS);
  const { state } = useProvider();
  const navigate = useNavigate();
  const { showModal } = useModal();
  const [query, setQuery] = useQueryParams();
  const { isOpen } = useTour();

  const searchboxRef = useRef<ElementRef<'input'>>(null);

  const { ref, inView } = useInView();

  const [startDueAt, setStartDueAt] = useState<Date | undefined>(undefined);
  const [endDueAt, setEndDueAt] = useState<Date | undefined>(undefined);
  const [usersSearch, setUsersSearch] = useState('');
  const [filterOptions, setFilterOptions] = useState(FILTER_OPTIONS);
  const [isBoardView, setIsBoardView] = useState(true);

  const {
    data: tasks,
    hasNextPage,
    fetchNextPage,
    isLoading,
  } = useGetTasks({ ..._c.TaskFilter, ...query });
  const { data: accountUserData } = useGetAccountUsers({
    keyword: usersSearch,
  });

  const dueAt = useMemo(
    () =>
      startDueAt && endDueAt
        ? {
            $gte: dayjs(startDueAt).toDate(),
            $lt: dayjs(endDueAt).add(1, 'days').toDate(),
          }
        : {},
    [startDueAt, endDueAt],
  );

  useEffect(() => {
    let accountUserMap: { itemLabel: string; value: string }[] = [];

    if (accountUserData?.data?.length) {
      accountUserMap = accountUserData?.data.map((user) => ({
        itemLabel: user.fullName,
        value: user._id,
      }));
    }

    setFilterOptions((prevVal) => ({
      ...prevVal,
      accountUserId: accountUserMap,
    }));
  }, [accountUserData]);

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  useEffect(() => {
    if (size(dueAt) > 0) {
      setQuery((prev) => ({
        ...prev,
        dueAt: JSON.stringify(dueAt),
      }));
    }
  }, [dueAt, setQuery]);

  const handleFilterOnChange = (filters: { [key: string]: any }) => {
    setQuery((prev) => ({
      ...prev,
      ...(size(dueAt) > 0 && { dueAt: JSON.stringify(dueAt) as string }),
      ...sanitizeParams(filters),
    }));
  };

  const handleSelectStatus = (status: Task['status']) => {
    setQuery((prev) => ({
      ...prev,
      ...(sanitizeParams({ ...query, status }) as any), // TODO: Type this properly
    }));
  };

  const handleCreateTasks = () => {
    showModal({
      title: 'Create Task',
      content: <CreateTaskModal params={query} />,
      disableOutsideClick: true,
    });
  };

  const onChangeDueAt = (dates: (Date | undefined)[]) => {
    const [start, end] = dates;
    setStartDueAt(start);
    setEndDueAt(end);
  };

  const accountUsersOptions = useCallback(
    (values: string[]) =>
      accountUserData?.data?.map((users) => ({
        value: users._id,
        label: users.fullName,
        description: users.emailAddress,
        isSelected: values?.includes(users._id),
        withCheckbox: true,
      })) ?? [],
    [accountUserData?.data],
  );

  const taskTypeOptions = useCallback(
    (values: string[]) =>
      filterOptions.type.map((type) => ({
        value: type.value,
        label: type.label,
        isSelected: values?.includes(type.value),
      })) ?? [],
    [filterOptions.type],
  );

  const handleSearch = debounce((val: string) => setUsersSearch(val), 300);

  const clearAssignees = (handleChange: (key: string, value: any) => void) => {
    setUsersSearch('');
    handleChange('accountUserId', []);
  };

  const clearUpdatedAt = (handleChange: (key: string, value: any) => void) => {
    handleChange('dueAt', undefined);
    setStartDueAt(undefined);
    setEndDueAt(undefined);
  };

  const clearAllFilters = (handleChange: (key: string, value: any) => void) => {
    clearAssignees(handleChange);
    clearUpdatedAt(handleChange);
    handleChange('keyword', '');
    handleChange('type', '');
    searchboxRef.current!.value = '';
  };

  const filterInit = useMemo(() => !isEmpty(query), [query]);

  return (
    <>
      <p className="mb-[22px] text-[16px] font-semibold">Tasks</p>
      <PageHeader
        filters={query}
        {...(canAdd && { onClickCreate: handleCreateTasks })}
        onFilterChange={handleFilterOnChange}
        isBoardView={isBoardView}
        onToggleView={setIsBoardView}
        type="task"
        createDataTut={TOUR_SELECTOR.TOUR.SALES.TASK.FORM.CREATE_BUTTON}
        settingItems={[
          {
            text: 'View archived',
            key: 'view',
            onClick: () => navigate('archived'),
          },
        ]}
      >
        {({ values, handleChange }) => (
          <>
            <div
              className={cn(
                'border-b-[0.5px] border-separator-primary',
                'pb-4',
              )}
            >
              <Tabs
                size={SIZE.Small}
                type="tertiary"
                items={filterOptions.status}
                onChange={({ value: status }) => handleSelectStatus(status)}
                selectedTab={
                  (query.status as string) || filterOptions.status[0].value
                }
              />
            </div>
            <div className="flex gap-x-3 pt-4">
              <Searchbox
                ref={searchboxRef}
                className="sm w-[200px]"
                name="keyword"
                placeholder="Search task title"
                defaultValue={values.keyword}
                onChange={debounce(
                  (event: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange('keyword', event.target.value);
                  },
                  300,
                )}
              />
              <Filter
                isMulti
                placeholder="Assignee"
                defaultValue={values.accountUserId}
                options={accountUsersOptions(values.accountUserId)}
                withSearch
                onSearch={handleSearch}
                onClear={() => {
                  setUsersSearch('');
                  handleChange('accountUserId', []);
                }}
                onChange={(opt) => {
                  const selectedUsers = values.accountUserId;
                  if (!selectedUsers) {
                    handleChange('accountUserId', [opt.value]);
                    return;
                  }

                  let accountUsers = isString(selectedUsers)
                    ? [selectedUsers]
                    : [...selectedUsers];

                  if (accountUsers.includes(opt.value)) {
                    accountUsers = [...selectedUsers].filter(
                      (id) => id !== opt.value,
                    );
                  } else {
                    accountUsers.push(opt.value);
                  }

                  handleChange('accountUserId', accountUsers);
                }}
              />
              <Dropdown
                placeholder="Task Type"
                options={taskTypeOptions(values.type)}
                defaultValue={values.type}
                value={values.type}
                onChange={(opt) => handleChange('type', opt.value)}
              />
              <Filter
                placeholder="Due At"
                options={DATE_OPTIONS}
                isDateField
                defaultValue={values.dueAt}
                startDate={startDueAt}
                endDate={endDueAt}
                onChangeDate={(dates) => {
                  onChangeDueAt(dates);
                }}
                onClear={() => {
                  handleChange('dueAt', undefined);
                  setStartDueAt(undefined);
                  setEndDueAt(undefined);
                }}
                onChange={(opt) => handleChange('dueAt', opt.value)}
              />
              {filterInit && (
                <Button
                  intent={'text'}
                  label="Clear Filters"
                  onClick={() => clearAllFilters(handleChange)}
                />
              )}
            </div>
          </>
        )}
      </PageHeader>
      <div
        className={cn(
          state.isOpenBanner
            ? 'h-[calc(100vh-393px)]'
            : 'h-[calc(100vh-335px)]',
          'mt-[22px] overflow-y-auto',
        )}
      >
        {isBoardView ? (
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 2xl:grid-cols-6">
            {isOpen ? (
              <TaskCard
                task={{
                  _id: 'test_task',
                  id: 'test_task',
                  accountId: '',
                  dueAt: new Date().toISOString(),
                  entryType: 'task',
                  title: 'Demo Task',
                  content: 'Demo Content',
                  leadId: DEMO_CONTACT.value,
                  accountUserId: [DEMO_ACCOUNT_USER.value],
                  priority: 'HIGH',
                  type: 'TODO',
                  lead: DEMO_CONTACT as unknown as Lead,
                  status: 'PENDING',
                  accountUsers: [DEMO_ACCOUNT_USER as any],
                }}
                isLoading={isLoading}
              />
            ) : (
              tasks?.pages
                ?.flatMap((task) => task.data)
                .map((task) => (
                  <TaskCard key={task._id} task={task} isLoading={isLoading} />
                ))
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {tasks?.pages
              ?.flatMap((task) => task.data)
              .map((task) => (
                <TaskTile key={task._id} task={task} isLoading={isLoading} />
              ))}
          </div>
        )}
        <p ref={ref} className="text-center text-sm">
          {hasNextPage ? 'Fetching...' : ' '}
        </p>
      </div>
    </>
  );
};

export default Tasks;
